import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/useCustomScrollbar";
import { useAuth } from "../../context/auth/AuthContext";
import { BASE_URL } from "../api/url";
import { getTotalActiveClients } from "../../services/dashboard";
import PageHeader from "../Ui_Components/PageHeader";
import Pagination from "../Ui_Components/Pagination";
import useCustomScrollbar from "../styles/useCustomScrollbar";

interface MealLogCount {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
}

interface MyClient {
  user_id: number;
  user_name: string;
  user_profile: string | null;
  goal: string;
  meal_log_count: MealLogCount;
  meal_log_status: string;
  subscription_period: string;
  subscription_type: string;
  activity_days: number;
  primary_nutrist_name: string;
  backup_nutrist_names: string | null;
  nutritionist_names: string[];
  purchase_date: string;
  duration_days: number;
  subscription_end_date: string;
  days_left: number;
  plan_status: string;
}

interface ClientListResponse {
  success: string;
  message: string;
  total_count: number;
  total_pages: number;
  current_page: number;
  data: MyClient[];
}

type TabType = "myClients" | "newAssigned" | "deactivate";

// Global cache - persists across component unmounts
const myClientsPageCache = new Map<number, MyClient[]>();
let cachedMyClientsTotalPages = 1;
let cachedMyClientsTotalCount = 0;

// Cache for New Assigned tab
const newAssignedPageCache = new Map<number, MyClient[]>();
let cachedNewAssignedTotalPages = 1;
let cachedNewAssignedTotalCount = 0;

// Cache for Deactivate tab
const deactivatePageCache = new Map<number, MyClient[]>();
let cachedDeactivateTotalPages = 1;
let cachedDeactivateTotalCount = 0;

const ClientPage: React.FC = () => {
  useCustomScrollbar();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Helper function to trim subscription type
  const trimSubscriptionType = (subscriptionType: string): string => {
    return subscriptionType.replace(/\s*\([^)]*\)/g, "").trim();
  };

  const [activeTab, setActiveTab] = useState<TabType>("myClients");
  const [myClients, setMyClients] = useState<MyClient[]>([]);
  const [newAssigned, setNewAssigned] = useState<MyClient[]>([]);
  const [deactivate, setDeactivate] = useState<MyClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MyClient[] | null>(null); // null = no active search
  const [searchLoading, setSearchLoading] = useState(false);
  const clientsPerPage = 10;

  // Track prefetch operations to avoid duplicates
  const prefetchingPages = useRef(new Set<number>());
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle navigation state from Dashboard
  useEffect(() => {
    if (location.state && location.state.activeTab) {
      setActiveTab(location.state.activeTab);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Fetch clients data from API (works for all three tabs)
  const fetchClients = async (
    page: number,
    status: "active" | "new_assigned" | "deactive",
    isPrefetch = false,
    search = "",
  ): Promise<void> => {
    if (!user?.nutrist_id) {
      if (!isPrefetch) {
        setLoading(false);
      }
      return;
    }

    const cache =
      status === "active"
        ? myClientsPageCache
        : status === "new_assigned"
          ? newAssignedPageCache
          : deactivatePageCache;

    // Skip cache entirely when searching
    if (!search && cache.has(page)) {
      if (!isPrefetch) {
        const data = cache.get(page)!;
        if (status === "active") {
          setMyClients(data);
          setTotalPages(cachedMyClientsTotalPages);
          setTotalCount(cachedMyClientsTotalCount);
        } else if (status === "new_assigned") {
          setNewAssigned(data);
          setTotalPages(cachedNewAssignedTotalPages);
          setTotalCount(cachedNewAssignedTotalCount);
        } else {
          setDeactivate(data);
          setTotalPages(cachedDeactivateTotalPages);
          setTotalCount(cachedDeactivateTotalCount);
        }
        setLoading(false);
      }
      return;
    }

    if (isPrefetch && prefetchingPages.current.has(page)) {
      return;
    }

    try {
      if (isPrefetch) {
        prefetchingPages.current.add(page);
      } else {
        setLoading(true);
      }

      const data: ClientListResponse = await getTotalActiveClients(
        user.nutrist_id,
        status,
        page,
        search,
      );

      if (data.success === "1") {
        // Only cache when not searching
        if (!search) {
          cache.set(page, data.data);

          if (status === "active") {
            cachedMyClientsTotalPages = data.total_pages;
            cachedMyClientsTotalCount = data.total_count;
          } else if (status === "new_assigned") {
            cachedNewAssignedTotalPages = data.total_pages;
            cachedNewAssignedTotalCount = data.total_count;
          } else {
            cachedDeactivateTotalPages = data.total_pages;
            cachedDeactivateTotalCount = data.total_count;
          }
        }

        if (!isPrefetch) {
          if (status === "active") {
            setMyClients(data.data);
            if (activeTab === "myClients") {
              setTotalPages(data.total_pages);
              setTotalCount(data.total_count);
            }
          } else if (status === "new_assigned") {
            setNewAssigned(data.data);
            if (activeTab === "newAssigned") {
              setTotalPages(data.total_pages);
              setTotalCount(data.total_count);
            }
          } else {
            setDeactivate(data.data);
            if (activeTab === "deactivate") {
              setTotalPages(data.total_pages);
              setTotalCount(data.total_count);
            }
          }
          setLoading(false);
        }

        // Only prefetch next pages when not searching
        if (!isPrefetch && !search && page < data.total_pages) {
          setTimeout(() => fetchClients(page + 1, status, true), 100);
        }
      } else {
        console.error("Failed to fetch client list:", data.message);
        if (!isPrefetch) {
          setLoading(false);
        }
      }
    } catch (err) {
      console.error(`Error fetching page ${page}:`, err);
      if (!isPrefetch) {
        setLoading(false);
      }
    } finally {
      if (isPrefetch) {
        prefetchingPages.current.delete(page);
      }
    }
  };

  // Load all tabs data on initial mount
  useEffect(() => {
    if (user?.nutrist_id) {
      fetchClients(1, "active", false);
      fetchClients(1, "new_assigned", false);
      fetchClients(1, "deactive", false);
    }
  }, [user?.nutrist_id]);

  // Load data when page changes (for current active tab only)
  useEffect(() => {
    if (currentPage === 1) {
      if (activeTab === "myClients" && myClientsPageCache.has(1)) {
        setMyClients(myClientsPageCache.get(1)!);
      } else if (activeTab === "newAssigned" && newAssignedPageCache.has(1)) {
        setNewAssigned(newAssignedPageCache.get(1)!);
      } else if (activeTab === "deactivate" && deactivatePageCache.has(1)) {
        setDeactivate(deactivatePageCache.get(1)!);
      }
      return;
    }

    const status =
      activeTab === "myClients"
        ? "active"
        : activeTab === "newAssigned"
          ? "new_assigned"
          : "deactive";

    fetchClients(currentPage, status, false);
  }, [currentPage, activeTab]);

  // Reset to page 1 and update totalPages/totalCount when tab changes
  // Also clear search state so each tab starts fresh
  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery("");
    setSearchResults(null);

    if (activeTab === "myClients") {
      setTotalPages(cachedMyClientsTotalPages);
      setTotalCount(cachedMyClientsTotalCount);
    } else if (activeTab === "newAssigned") {
      setTotalPages(cachedNewAssignedTotalPages);
      setTotalCount(cachedNewAssignedTotalCount);
    } else {
      setTotalPages(cachedDeactivateTotalPages);
      setTotalCount(cachedDeactivateTotalCount);
    }
  }, [activeTab]);

  // Debounced API search — fires whenever searchQuery or activeTab changes
 useEffect(() => {
  if (!searchQuery.trim()) {
    setSearchResults(null);
    setCurrentPage(1);
    return;
  }

  const status = // (ClientPage only — use the activeTab-based status)
    activeTab === "myClients"
      ? "active"
      : activeTab === "newAssigned"
        ? "new_assigned"
        : "deactive";

  const timer = setTimeout(async () => {
    if (!user?.nutrist_id) return;
    setSearchLoading(true);
    try {
      const data: ClientListResponse = await getTotalActiveClients(
        user.nutrist_id,
        status,       // "active" for Dashboard, tab-based for ClientPage
        1,
        searchQuery.trim(),
      );
      setSearchResults(data.success === "1" ? data.data : []);
    } catch (err) {
      console.error("Search error:", err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, 600); // 600ms — fires only after user stops typing

  return () => clearTimeout(timer); // ← clears on every new keystroke
}, [searchQuery, activeTab, user?.nutrist_id]); // (Dashboard omits activeTab)
  // Navigate to client detail page
  const handleClientClick = (userId: number, isDeactivated = false) => {
  navigate(`/client/${userId}`, { state: { isDeactivated } });
};

  // Returns the correct data depending on whether a search is active
  const getCurrentData = (): MyClient[] => {
    if (searchResults !== null) return searchResults;

    switch (activeTab) {
      case "myClients":
        return myClients;
      case "newAssigned":
        return newAssigned;
      case "deactivate":
        return deactivate;
      default:
        return [];
    }
  };

  const currentData = getCurrentData();
  const paginatedData = currentData;

  // Get meal log dots based on meal_log_count
  const getMealLogDots = (mealLogCount: MealLogCount) => {
    const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
    const dots = [];

    const currentDayIndex = new Date().getDay();
    const adjustedCurrentDay = currentDayIndex === 0 ? 6 : currentDayIndex - 1;

    for (let i = 0; i < 7; i++) {
      const dayCount = mealLogCount[days[i]];
      const isCurrentDay = i === adjustedCurrentDay;
      const isFutureDay = i > adjustedCurrentDay;

      if (isCurrentDay) {
        dots.push(
          <div
            key={i}
            className="w-3 h-3 rounded-full flex items-center justify-center"
            style={{ border: "4px solid #0DB5B5" }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: "#ffffff" }}
            ></div>
          </div>,
        );
      } else if (isFutureDay) {
        dots.push(
          <div
            key={i}
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: "#D9D9D9" }}
          ></div>,
        );
      } else {
        dots.push(
          <div
            key={i}
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor: dayCount === 1 ? "#00B32D" : "#FFA628",
            }}
          ></div>,
        );
      }
    }
    return dots;
  };

  // Get user avatar or initials
  const getUserAvatar = (client: MyClient) => {
    if (client.user_profile) {
      return (
        <img
          src={`${BASE_URL}${client.user_profile}`}
          alt={client.user_name}
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
        />
      );
    }

    const initials = client.user_name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    return (
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center text-white text-sm sm:text-base font-semibold flex-shrink-0">
        {initials}
      </div>
    );
  };

  const getTabCount = (tab: TabType) => {
    switch (tab) {
      case "myClients":
        return cachedMyClientsTotalCount || myClients.length;
      case "newAssigned":
        return cachedNewAssignedTotalCount || newAssigned.length;
      case "deactivate":
        return cachedDeactivateTotalCount || deactivate.length;
      default:
        return 0;
    }
  };

  // Empty state message per tab
  const getEmptyMessage = (tab: TabType) => {
    switch (tab) {
      case "myClients":
        return searchQuery.trim()
          ? "No active clients match your search"
          : "No active clients available";
      case "newAssigned":
        return searchQuery.trim()
          ? "No new assigned clients match your search"
          : "No new assigned clients available";
      case "deactivate":
        return searchQuery.trim()
          ? "No deactivated clients match your search"
          : "No deactivated clients available";
    }
  };

  const isSearchActive = searchQuery.trim().length > 0;

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA]">
      {/* Header */}
      <PageHeader title="Clients" />

      {/* Tabs and Content */}
      <div className="px-4 sm:px-6 lg:px-8 pb-4 flex-1 flex flex-col overflow-hidden">
        {/* Tabs */}
        <div className="flex gap-4 mb-4 border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setActiveTab("myClients")}
            className={`pb-3 px-1 relative text-sm sm:text-base font-medium transition-colors ${
              activeTab === "myClients"
                ? "text-[#1F1F1F]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            My Clients{" "}
            <span
              className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                activeTab === "myClients"
                  ? "bg-[#00A9A9] text-white"
                  : "bg-gray-400 text-white"
              }`}
            >
              {getTabCount("myClients")}
            </span>
            {activeTab === "myClients" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00A9A9]"></div>
            )}
          </button>

          <button
            onClick={() => setActiveTab("newAssigned")}
            className={`pb-3 px-1 relative text-sm sm:text-base font-medium transition-colors ${
              activeTab === "newAssigned"
                ? "text-[#00A9A9]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            New Assigned{" "}
            <span
              className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                activeTab === "newAssigned"
                  ? "bg-[#00A9A9] text-white"
                  : "bg-gray-400 text-white"
              }`}
            >
              {getTabCount("newAssigned")}
            </span>
            {activeTab === "newAssigned" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00A9A9]"></div>
            )}
          </button>

          <button
            onClick={() => setActiveTab("deactivate")}
            className={`pb-3 px-1 relative text-sm sm:text-base font-medium transition-colors ${
              activeTab === "deactivate"
                ? "text-[#1F1F1F]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Deactivate{" "}
            <span
              className={`ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                activeTab === "deactivate"
                  ? "bg-[#00A9A9] text-white"
                  : "bg-gray-400 text-white"
              }`}
            >
              {getTabCount("deactivate")}
            </span>
            {activeTab === "deactivate" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00A9A9]"></div>
            )}
          </button>
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-[6px] shadow-sm flex-1 flex flex-col border-[0.5px] border-[#F3F4F6] overflow-hidden">
          {/* Fixed Header with Search */}
          <div className="p-4 sm:px-6 sm:py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 flex-shrink-0">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
              {activeTab === "myClients" &&
                `Total active clients (${
                  isSearchActive
                    ? currentData.length
                    : getTabCount("myClients")
                })`}
              {activeTab === "newAssigned" &&
                `Total new assigned clients (${
                  isSearchActive
                    ? currentData.length
                    : getTabCount("newAssigned")
                })`}
              {activeTab === "deactivate" &&
                `Total deactivate (${
                  isSearchActive
                    ? currentData.length
                    : getTabCount("deactivate")
                })`}
            </h3>
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-400 text-sm"
              />
              <Search className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {/* Scrollable Table Container */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {loading || searchLoading ? (
              <div className="flex items-center justify-center flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-base text-gray-500">Loading data</p>
                </div>
              </div>
            ) : (
              <>
                {/* ───── My Clients Table ───── */}
                {activeTab === "myClients" && (
                  <>
                    <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5 custom-scrollbar-overlay">
                      <table className="w-full min-w-[640px] table-fixed">
                        <thead>
                          <tr className="bg-white">
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[30%]">
                              Client name
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[19%]">
                              Goal
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[18%]">
                              Activity
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[18%]">
                              Meal log
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[15%]">
                              Status
                            </th>
                          </tr>
                        </thead>
                      </table>
                    </div>

                    <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
                      {paginatedData.length === 0 ? (
                        <div className="flex items-center justify-center h-full py-16">
                          <p className="text-gray-400 text-sm sm:text-base font-inter">
                            {getEmptyMessage("myClients")}
                          </p>
                        </div>
                      ) : (
                        <table className="w-full min-w-[640px] table-fixed">
                          <tbody>
                            {paginatedData.map((client, index) => {
                              const myClient = client as MyClient;
                              return (
                                <tr
                                  key={myClient.user_id}
                                  onClick={() =>
                                    handleClientClick(myClient.user_id)
                                  }
                                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                                >
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 w-[30%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      {getUserAvatar(myClient)}
                                      <div>
                                        <div className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base">
                                          {myClient.user_name}
                                        </div>
                                        <div className="text-xs sm:text-sm font-inter text-gray-500">
                                          {trimSubscriptionType(
                                            myClient.subscription_type,
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[19%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {myClient.goal}
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[18%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {myClient.activity_days}/
                                    {myClient.duration_days}
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 w-[18%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex gap-1">
                                      {getMealLogDots(myClient.meal_log_count)}
                                    </div>
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 w-[15%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    <span
                                      className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-[12px] font-semibold font-inter ${
                                        myClient.plan_status === "active"
                                          ? "bg-[#EBF7F4] text-[#0DB5B5]"
                                          : myClient.plan_status ===
                                              "ending_soon"
                                            ? "bg-[#FFF3DE] text-[#FF9500]"
                                            : "bg-gray-100 text-gray-600"
                                      }`}
                                    >
                                      {myClient.plan_status
                                        .split("_")
                                        .map(
                                          (word) =>
                                            word.charAt(0).toUpperCase() +
                                            word.slice(1),
                                        )
                                        .join(" ")}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}

                {/* ───── New Assigned Table ───── */}
                {activeTab === "newAssigned" && (
                  <>
                    <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5 custom-scrollbar-overlay">
                      <table className="w-full min-w-[640px] table-fixed">
                        <thead>
                          <tr className="bg-white">
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[25%]">
                              Client name
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[20%]">
                              Plan name
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[20%]">
                              Goal
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[20%]">
                              Joining date
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[15%]">
                              Duration
                            </th>
                          </tr>
                        </thead>
                      </table>
                    </div>

                    <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
                      {paginatedData.length === 0 ? (
                        <div className="flex items-center justify-center h-full py-16">
                          <p className="text-gray-400 text-sm sm:text-base font-inter">
                            {getEmptyMessage("newAssigned")}
                          </p>
                        </div>
                      ) : (
                        <table className="w-full min-w-[640px] table-fixed">
                          <tbody>
                            {paginatedData.map((client, index) => {
                              const newClient = client as MyClient;
                              return (
                                <tr
                                  key={newClient.user_id}
                                  onClick={() =>
                                    handleClientClick(newClient.user_id)
                                  }
                                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                                >
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 w-[25%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      {getUserAvatar(newClient)}
                                      <div className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base">
                                        {newClient.user_name}
                                      </div>
                                    </div>
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[20%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {trimSubscriptionType(
                                      newClient.subscription_type,
                                    )}
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[20%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {newClient.goal}
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[20%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {newClient.purchase_date}
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[15%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {newClient.subscription_period}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}

                {/* ───── Deactivate Table ───── */}
                {activeTab === "deactivate" && (
                  <>
                    <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] mx-5 custom-scrollbar-overlay">
                      <table className="w-full min-w-[640px] table-fixed">
                        <thead>
                          <tr className="bg-white">
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[30%]">
                              Client name
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[25%]">
                              Goal
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[22.5%]">
                              Joining date
                            </th>
                            <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-[#646464] text-xs sm:text-sm font-inter w-[22.5%]">
                              End date
                            </th>
                          </tr>
                        </thead>
                      </table>
                    </div>

                    <div className="flex-1 overflow-x-auto overflow-y-auto mx-5 custom-scrollbar-overlay">
                      {paginatedData.length === 0 ? (
                        <div className="flex items-center justify-center h-full py-16">
                          <p className="text-gray-400 text-sm sm:text-base font-inter">
                            {getEmptyMessage("deactivate")}
                          </p>
                        </div>
                      ) : (
                        <table className="w-full min-w-[640px] table-fixed">
                          <tbody>
                            {paginatedData.map((client, index) => {
                              const deactClient = client as MyClient;
                              return (
                                <tr
                                  key={deactClient.user_id}
                                 onClick={() => handleClientClick(deactClient.user_id, true)}
                                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                                >
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 w-[30%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      {getUserAvatar(deactClient)}
                                      <div className="font-medium font-inter text-[#1F1F1F] text-sm sm:text-base">
                                        {deactClient.user_name}
                                      </div>
                                    </div>
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[25%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {deactClient.goal}
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[22.5%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {deactClient.purchase_date}
                                  </td>
                                  <td
                                    className={`py-3 sm:py-4 px-4 sm:px-6 text-[#1F1F1F] text-sm sm:text-base font-inter w-[22.5%] ${
                                      index !== paginatedData.length - 1
                                        ? "border-b border-[#111C2D1A]"
                                        : ""
                                    }`}
                                  >
                                    {deactClient.subscription_end_date}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Pagination Component — hidden while search is active */}
          {!isSearchActive && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              resultCount={paginatedData.length}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientPage;